import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Grid2, TextField, Typography, Autocomplete } from '@mui/material';
import { Formik, Form } from 'formik';
import { buildPackagePayload, getPackageConfigConfigURL, getPackageDetailsURL, initialValues, MasterDataResponse, PackageDetailsValues, PackageTariffItem, sourceTariffItem, validationSchema, normalizeTariff } from './PackageConfigHelper';
import { getRequest, postRequest, putRequest } from '../../../services/httpService';
import { useAppQuery } from '../../../services/appQuery';
import { useAlert } from '../../../components/alertProvider';
import { ConfirmationModal } from '../../../components/confirmationModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NewPackageViewMode from './NewPackageViewMode';
import { useClinic } from '../../../components/ClinicProvider';
import MultiSelectAutocompleteWithChips from '../../Inventory/ReusableComponents/MultiSelectAutocompleteWithChips';
import SelectedChip from '../../Inventory/ReusableComponents/selectedChip';
import {  filterInputSubLabelSx } from '../../Inventory/Common/inventory.helper';
import { Tariff, useTariffOptions } from '../../../components/Billing/Tariff/tariffHelper.helpers';
import { todayISO } from '../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { RequiredLabel } from '../../../utils/commonUtils';


interface NewPackageProps {
  onStepComplete?: (packageData: PackageDetailsValues) => void;
  packageIdFromStepper?: number | null;
  onValidationChange?: (isValid: boolean) => void;
  onTariffsAvailable?: (hasTariffs: boolean, packageData: PackageDetailsValues) => void;
}

const NewPackage: React.FC<NewPackageProps> = ({ onStepComplete, packageIdFromStepper, onValidationChange, onTariffsAvailable }) => {
  const { addAlert }  = useAlert();
  const { t: tBilling } = useTranslation('billing_config');
  const [searchParams] = useSearchParams();
  const packageIdParam = searchParams.get('packageId');
  const parsedPackageId = packageIdParam ? Number(packageIdParam) : undefined;
  // Use packageIdFromStepper if available (coming from PackageConfigurationFlow after save), otherwise use URL param
  const packageId = packageIdFromStepper ?? (parsedPackageId !== undefined && Number.isFinite(parsedPackageId) ? parsedPackageId : undefined);
  
  // Initialize isViewMode to true if we have a packageId (editing existing package)
  // This prevents flash of edit form before view form loads
  const [isViewMode, setIsViewMode] = React.useState(Boolean(packageId));
  const [viewPackage, setViewPackage] = React.useState<PackageDetailsValues | null>(null);
  const lastIsValidRef = React.useRef<boolean | null>(null);
  const onValidationChangeRef = React.useRef(onValidationChange); // Ref to store latest callback
  const navigate = useNavigate();
  const [pendingValues, setPendingValues] = useState<PackageDetailsValues | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const headerTitle = packageId ? 'Edit Package' : 'New Package';
  const { ClinicId } = useClinic();
  const { tariffs, isLoading: isTariffLoading , isError: tariffError } = useTariffOptions();
  const [ mappedTariffs , setMappedTariffs ] = useState<PackageTariffItem[]>([]);
  const { data: existingPackage, isFetched: isExistingPackageFetched, isError: isExistingPackageError } =
  useAppQuery<PackageDetailsValues>(
    ['PackageDetails', packageId],
    () => getRequest<PackageDetailsValues>(`${getPackageDetailsURL()}${packageId}/`),
    {
      enabled: Boolean(packageId),
      staleTime:0,
      gcTime:0,
      retry: false,
    }
  );

  


  useEffect(()=>{

    if (tariffError ){
      addAlert(
        tBilling('package_config.messages.failed_to_load_tariffs', {
          defaultValue: 'Failed to load tariffs: {{error}}',
          error: String(tariffError),
        }),
        'error'
      );
      return;
      
    };


    if (!isTariffLoading && tariffs){
      // Map incoming Tariff objects to PackageTariffItem[] (ensure `label` exists)
      setMappedTariffs(tariffs.map((t : Tariff) => ({
        id: t.id,
        label: t.name,
        name: t.name,
        code: t.code,
        description: t.description ?? undefined,
      })));
    }

    

  },[tariffs, isTariffLoading, tariffError, addAlert, tBilling]);



  const initialFormikValues = useMemo(
    () => {
      const source: PackageDetailsValues | null = viewPackage ?? existingPackage ?? null;
      const numericClinicId = ClinicId != null && !Number.isNaN(Number(ClinicId))
        ? Number(ClinicId)
        : undefined;

      return {
        ...initialValues,
        ...(source ?? {}),
        id: packageId ?? source?.id ?? null,
        clinic: source?.clinic ?? numericClinicId ?? initialValues.clinic,
        // class_rates: (source?.class_rates ?? initialValues.class_rates ?? []).map((cr) => ({
        //   ...cr,
        //   enabled:
        //     cr.enabled !== undefined
        //       ? cr.enabled
        //       : cr.rate !== '' && cr.rate !== null && cr.rate !== undefined,
        // })),
        tariffs: (source?.tariffs ?? initialValues.tariffs ?? []).map((cr: sourceTariffItem | PackageTariffItem) => normalizeTariff(cr))
      };
    },
    [packageId, existingPackage, viewPackage, ClinicId]
  );

  const { data: PackageConfig, queryClient } = useAppQuery<MasterDataResponse>(
    ['PackageConfigMaster'],
    () => getRequest<MasterDataResponse>(`${getPackageConfigConfigURL()}`),
    { 
      enabled: true,
    }
  );

  const getRuleLabel = React.useCallback((ruleCode?: string, fallback?: string) => {
    if (ruleCode === 'CONCESSION') {
      return tBilling('package_config.concession', { defaultValue: 'Concession' });
    }
    if (ruleCode === 'RATE_EDIT') {
      return tBilling('package_config.rate_editable', { defaultValue: 'Rate Editable' });
    }
    if (ruleCode === 'DOCTOR_SHARE') {
      return tBilling('package_config.doctor_share', { defaultValue: 'Doctor Share' });
    }
    return fallback || '-';
  }, [tBilling]);

  // const { data: PackageClassMaster } = useAppQuery<PackageClassMasterResponse>(
  //   ['PackageClassMaster'],
  //   () => getRequest<PackageClassMasterResponse>(`${getClassMasterURL()}`),
  //   { 
  //     enabled: true,
  //     staleTime: 5 * 60 * 1000
  //   }
  // );




  const submitPackage = async (values: PackageDetailsValues) => {
    const payload = buildPackagePayload(values);
    const isUpdate = Boolean(existingPackage?.id ?? packageId);
    const url = isUpdate
      ? `${getPackageDetailsURL()}${existingPackage?.id ?? packageId}/`
      : getPackageDetailsURL();

    // Ensure IDs from the form/existing data are present in payload when updating
    const finalPayload = isUpdate
      ? { ...payload, id: existingPackage?.id ?? packageId }
      : payload;

    try {
      let savedPackage: PackageDetailsValues | null = null;
      let freshDetails: PackageDetailsValues | null = null;
      if (queryClient) {
        savedPackage = await queryClient.fetchQuery<PackageDetailsValues>({
          queryKey: ['PackageDetailsSubmit', isUpdate ? 'put' : 'post', existingPackage?.id ?? packageId ?? 'new'],
          queryFn: () => (isUpdate
            ? putRequest(url, finalPayload)
            : postRequest(url, finalPayload)),
          staleTime: 0,
        });
      } else {
        if (isUpdate) {
          savedPackage = await putRequest(url, finalPayload) as PackageDetailsValues;
        } else {
          savedPackage = await postRequest(url, finalPayload) as PackageDetailsValues;
        }
      }
      addAlert(
        tBilling('package_config.messages.package_saved_success', {
          defaultValue: 'Package details {{status}} successfully.',
          status: isUpdate
            ? tBilling('package_config.status.updated', { defaultValue: 'updated' })
            : tBilling('package_config.status.saved', { defaultValue: 'saved' }),
        }),
        'success'
      );
      // Prefer the ID returned from the save API response; fall back to existing IDs
      const savedId = (savedPackage as unknown as { id?: number })?.id ?? existingPackage?.id ?? packageId;

      if (savedId && queryClient) {
        freshDetails = await queryClient.fetchQuery<PackageDetailsValues>({
          queryKey: ['PackageDetails', savedId],
          queryFn: () => getRequest<PackageDetailsValues>(`${getPackageDetailsURL()}${savedId}/`),
          staleTime: 0,
        });
      }

      const packageForView = freshDetails ?? savedPackage;
      if (packageForView) {
        setViewPackage(packageForView);
        setIsViewMode(true);
        
        // Update URL with the package ID
        if (savedId) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('packageId', String(savedId));
          navigate(`?${newSearchParams.toString()}`, { replace: true });
        }
        
        // Call the callback to notify parent component about successful completion
        if (onStepComplete) {
          onStepComplete(packageForView);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      addAlert(
        tBilling('package_config.messages.failed_to_save_package', {
          defaultValue: 'Failed to save package: {{error}}',
          error: message,
        }),
        'error'
      );
    }
  };

  const handleFormikSubmit = (values: PackageDetailsValues) => {
    setPendingValues(values);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setPendingValues(null);
  };

  const handleConfirmedSubmit = async () => {
    if (!pendingValues) return;
    setOpenModal(false);
    await submitPackage(pendingValues);
    setPendingValues(null);
  };

  React.useEffect(() => {
    if (existingPackage) {
      setViewPackage(existingPackage);
      setIsViewMode(true);
      // Mark step as completed when existing package data loads in edit mode
      if (onValidationChangeRef.current) {
        onValidationChangeRef.current(true);
      }
      // Notify parent if tariffs data is available
      if (onTariffsAvailable && existingPackage.tariffs && existingPackage.tariffs.length > 0) {
        onTariffsAvailable(true, existingPackage);
      }
    } else if (isExistingPackageFetched && !existingPackage) {
      // No data exists (404 or empty response), switch to edit mode
      setIsViewMode(false);
    } else if (isExistingPackageError) {
      // Error fetching data, switch to edit mode
      setIsViewMode(false);
    }
  }, [existingPackage, onTariffsAvailable, isExistingPackageFetched, isExistingPackageError]);

  // Keep callback ref in sync with latest value (no dependency array needed)
  React.useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  });

  if (isViewMode){
    // Use viewPackage if available, otherwise use existingPackage (for initial load)
    const packageToDisplay = viewPackage ?? existingPackage;
    if (!packageToDisplay) return null;

    return(
      <NewPackageViewMode
        data={packageToDisplay}
        onBack={() => setIsViewMode(false)}
      />
    );
  }

  return (
    <Box sx={{ p: 2 }} data-testid="new-package-container">
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>{headerTitle}</Typography>

      <Formik<PackageDetailsValues>
        initialValues={initialFormikValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleFormikSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isValid }) => {
          // Track validation changes using ref to avoid calling hooks inside callback
          if (lastIsValidRef.current !== isValid) {
            lastIsValidRef.current = isValid;
            // Defer state update to avoid "Cannot update component while rendering" warning
            Promise.resolve().then(() => {
              if (packageId) {
                // Edit/view mode: pass the actual isValid state
                onValidationChangeRef.current?.(isValid);
              } else {
                // New mode: don't mark as completed until saved
                onValidationChangeRef.current?.(false);
              }
            });
          }

          return (
            <Form data-testid="newpkg-form">
              {/* Package Details */}
              <Box sx={{ mb: 3, borderBottom: '1px solid #E0E0E0', pb: 3 }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontFamily: 'Montserrat', fontSize: 16 }}>{tBilling('package_config.package_details', { defaultValue: 'Package Details' })}</Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField data-testid="newpkg-code-input" name="id" label={tBilling('package_config.headers.code', { defaultValue: 'Code' })} size="small" disabled fullWidth value={values.id || ''} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.id && errors.id)}  />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField data-testid="newpkg-name-input" name="package_name" label={RequiredLabel(tBilling('package_config.headers.package_name', { defaultValue: 'Package Name' }), true)} size="small" fullWidth value={values.package_name || ''} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.package_name && errors.package_name)} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField data-testid="newpkg-short-desc-input" name="short_description" label={(tBilling('package_config.short_description', { defaultValue: 'Short Description' }))} size="small" fullWidth value={values.short_description || ''} onChange={handleChange} onBlur={handleBlur} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField
                      data-testid="newpkg-long-desc-input"
                      name="long_description"
                      label={tBilling('package_config.long_description', { defaultValue: 'Long Description' })}
                      size="small"
                      fullWidth
                      value={values.long_description || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={2} sx={{ mt: 3 }}>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField data-testid="newpkg-amount-input" name="package_amount" 
                      label={RequiredLabel(tBilling('package_config.headers.package_amount', { defaultValue: 'Package Amount' }), true)} size="small" fullWidth type="number"
                      value={values.package_amount ?? ''} onChange={handleChange} 
                      onBlur={handleBlur} 
                      error={Boolean(touched.package_amount && errors.package_amount)} 
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField data-testid="newpkg-effective-date-input" 
                      name="effective_date" label={RequiredLabel(tBilling('package_config.headers.effective_date', { defaultValue: 'Effective Date' }), true)} size="small"
                      fullWidth type="date"
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: existingPackage?.effective_date ? {} : { min: todayISO() } }}
                      value={values.effective_date || ''} onChange={handleChange} onBlur={handleBlur} 
                      error={Boolean(touched.effective_date && errors.effective_date)} 
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <TextField data-testid="newpkg-expiry-date-input" 
                      name="expiry_date" label={RequiredLabel(tBilling('package_config.headers.expiry_date', { defaultValue: 'Expiry Date' }), true)} size="small"
                      fullWidth type="date"
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayISO() } }}
                      value={values.expiry_date || ''} onChange={handleChange} onBlur={handleBlur} 
                      error={Boolean(touched.expiry_date && errors.expiry_date)} 
                    />
                  </Grid2>
                </Grid2>
              </Box>

              {/* Package Level */}
              <Box sx={{ mb: 3 ,  borderBottom: '1px solid #E0E0E0', pb: 3 }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontFamily: 'Montserrat', fontSize: 16 }}>{tBilling('package_config.package_level', { defaultValue: 'Package Level' })}</Typography>
                <Grid2 container spacing={2}>
                  {[0,1].map((idx) => (
                    <Grid2 key={idx} size={{ xs: 12, md: 3 }}>
                      <Autocomplete
                        data-testid={`newpkg-level-${idx}-select`}
                        size="small"
                        options={
                          idx === 0 ? (PackageConfig?.level_1 || []) :
                            (PackageConfig?.level_2 || [])
                        }
                        getOptionLabel={(opt) => opt?.name ?? ''}
                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        value={(() => {
                          const code = values.levels?.[idx]?.level_code || '';
                          const list = idx === 0 ? (PackageConfig?.level_1 || []) :
                            (PackageConfig?.level_2 || []);
                          return list.find(o => o.code === code) ?? null;
                        })()}
                        onChange={(_, option) => {
                          setFieldValue(`levels.${idx}.level_code`, option?.code ?? '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={tBilling('package_config.level_with_index', { defaultValue: 'Level {{index}}', index: idx + 1 })}
                            placeholder={tBilling('package_config.placeholders.select_package_level', { defaultValue: 'Select Package Level' })}
                          />
                        )}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Box>

              {/* <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 3 }} /> */}

              {/* Package Rules */}
              <Box sx={{ mb: 3, borderBottom: '1px solid #E0E0E0', pb: 3 }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontFamily: 'Montserrat', fontSize: 16 }}>{tBilling('package_config.package_rules', { defaultValue: 'Package Rules' })}</Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {/* Concession */}
                  {(() => {
                    const idx = values.rules?.findIndex(r => r.rule_code === 'CONCESSION') ?? -1;
                    const rule = idx >= 0 ? values.rules![idx] : undefined;
                    return (
                      <Box sx={{ flex: 1, pl: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              data-testid="newpkg-concession-checkbox"
                              checked={Boolean(rule?.active)}
                              onChange={(e) => {
                                if (idx === -1) {
                                  const newRule = {
                                    rule_code: 'CONCESSION',
                                    rule_name: 'Concession',
                                    rule_description: 'Concession percentage limits',
                                    active: e.target.checked,
                                    min_percent: null,
                                    max_percent: null,
                                  };
                                  setFieldValue('rules', [...(values.rules ?? []), newRule]);
                                } else {
                                  setFieldValue(`rules.${idx}.active`, e.target.checked);
                                  // Reset values to null when deselecting
                                  if (!e.target.checked) {
                                    setFieldValue(`rules.${idx}.min_percent`, null);
                                    setFieldValue(`rules.${idx}.max_percent`, null);
                                  }
                                }
                              }}
                              sx={{ '&.Mui-checked': { color: '#66bb6a' } }}
                            />
                          }
                          label={getRuleLabel('CONCESSION', rule?.rule_name)}
                        />
                        <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid2 size={{ xs: 6 }}>
                            <TextField
                              data-testid="newpkg-concession-min-input"
                              name={`rules.${idx}.min_percent`}
                              label={tBilling('package_config.min_percent', { defaultValue: 'Min %' })}
                              size="small"
                              fullWidth
                              type="number"
                              placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                              value={rule?.min_percent ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!rule?.active}
                              error={Boolean(Array.isArray(touched.rules) && touched.rules[idx]?.min_percent && Array.isArray(errors.rules) && errors.rules[idx]?.min_percent)}
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 6 }}>
                            <TextField
                              data-testid="newpkg-concession-max-input"
                              name={`rules.${idx}.max_percent`}
                              label={tBilling('package_config.max_percent', { defaultValue: 'Max %' })}
                              size="small"
                              fullWidth
                              type="number"
                              placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                              value={rule?.max_percent ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!rule?.active}
                              error={Boolean(Array.isArray(touched.rules) && touched.rules[idx]?.max_percent && Array.isArray(errors.rules) && errors.rules[idx]?.max_percent)}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    );
                  })()}

                  {/* Doctor Share */}
                  {/* {(() => {
                    const idx = values.rules?.findIndex(r => r.rule_code === 'DOCTOR_SHARE') ?? -1;
                    const rule = idx >= 0 ? values.rules![idx] : undefined;
                    const packageAmount = Number(values.package_amount) || 0;
                    
                    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                      const percentage = Number(e.target.value) || 0;
                      setFieldValue(`rules.${idx}.percentage`, e.target.value);
                      if (packageAmount > 0) {
                        const calculatedAmount = (packageAmount * percentage) / 100;
                        setFieldValue(`rules.${idx}.amount`, Number(calculatedAmount.toFixed(2)));
                      }
                    };
                    
                    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                      const amount = Number(e.target.value) || 0;
                      setFieldValue(`rules.${idx}.amount`, e.target.value);
                      if (packageAmount > 0) {
                        const calculatedPercentage = (amount / packageAmount) * 100;
                        setFieldValue(`rules.${idx}.percentage`, Number(calculatedPercentage.toFixed(2)));
                      }
                    };
                    
                    return (
                      <Box sx={{ flex: 1, borderLeft: '1px solid #E0E0E0', pl: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(rule?.active)}
                              onChange={(e) => {
                                if (idx === -1) {
                                  const newRule = {
                                    rule_code: 'DOCTOR_SHARE',
                                    rule_name: 'Doctor Share',
                                    rule_description: 'Doctor share percentage and amount',
                                    active: e.target.checked,
                                    percentage: null,
                                    amount: null,
                                  };
                                  setFieldValue('rules', [...(values.rules ?? []), newRule]);
                                } else {
                                  setFieldValue(`rules.${idx}.active`, e.target.checked);
                                  // Reset values to null when deselecting
                                  if (!e.target.checked) {
                                    setFieldValue(`rules.${idx}.percentage`, null);
                                    setFieldValue(`rules.${idx}.amount`, null);
                                  }
                                }
                              }}
                              sx={{ '&.Mui-checked': { color: '#66bb6a' } }}
                            />
                          }
                          label={rule?.rule_name || 'Doctor Share'}
                        />
                        <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid2 size={{ xs: 6 }}>
                            <TextField
                              name={`rules.${idx}.percentage`}
                              label="Percentage. (%)"
                              size="small"
                              fullWidth
                              type="number"
                              placeholder="Enter"
                              value={rule?.percentage ?? ''}
                              onChange={handlePercentageChange}
                              onBlur={handleBlur}
                              disabled={!rule?.active}
                              error={Boolean(Array.isArray(touched.rules) && touched.rules[idx]?.percentage && Array.isArray(errors.rules) && errors.rules[idx]?.percentage)}
                              helperText={Array.isArray(touched.rules) && touched.rules[idx]?.percentage && Array.isArray(errors.rules) && errors.rules[idx]?.percentage ? String(errors.rules[idx]?.percentage) : ''}
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 6 }}>
                            <TextField
                              name={`rules.${idx}.amount`}
                              label="Amount"
                              size="small"
                              fullWidth
                              type="number"
                              placeholder="Enter"
                              value={rule?.amount ?? ''}
                              onChange={handleAmountChange}
                              onBlur={handleBlur}
                              disabled={!rule?.active}
                              error={Boolean(Array.isArray(touched.rules) && touched.rules[idx]?.amount && Array.isArray(errors.rules) && errors.rules[idx]?.amount)}
                              helperText={Array.isArray(touched.rules) && touched.rules[idx]?.amount && Array.isArray(errors.rules) && errors.rules[idx]?.amount ? String(errors.rules[idx]?.amount) : ''}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    );
                  })()} */}

                  {/* Rate Editable */}
                  {(() => {
                    const idx = values.rules?.findIndex(r => r.rule_code === 'RATE_EDIT') ?? -1;
                    const rule = idx >= 0 ? values.rules![idx] : undefined;
                    return (
                      <Box sx={{ flex: 1, borderLeft: '1px solid #E0E0E0', pl: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              data-testid="newpkg-rate-edit-checkbox"
                              checked={Boolean(rule?.active)}
                              onChange={(e) => {
                                if (idx === -1) {
                                  const newRule = {
                                    rule_code: 'RATE_EDIT',
                                    rule_name: 'Rate Editable',
                                    rule_description: 'Rate edit min/max amount',
                                    active: e.target.checked,
                                    min_amount: null,
                                    max_amount: null,
                                  };
                                  setFieldValue('rules', [...(values.rules ?? []), newRule]);
                                } else {
                                  setFieldValue(`rules.${idx}.active`, e.target.checked);
                                  // Reset values to null when deselecting
                                  if (!e.target.checked) {
                                    setFieldValue(`rules.${idx}.min_amount`, null);
                                    setFieldValue(`rules.${idx}.max_amount`, null);
                                  }
                                }
                              }}
                              sx={{ '&.Mui-checked': { color: '#66bb6a' } }}
                            />
                          }
                          label={getRuleLabel('RATE_EDIT', rule?.rule_name)}
                        />
                        <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid2 size={{ xs: 6 }}>
                            <TextField
                              data-testid="newpkg-rate-edit-min-input"
                              name={`rules.${idx}.min_amount`}
                              label={tBilling('package_config.min_amt', { defaultValue: 'Min Amt' })}
                              size="small"
                              fullWidth
                              type="number"
                              placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                              value={rule?.min_amount ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!rule?.active}
                              error={Boolean(Array.isArray(touched.rules) && touched.rules[idx]?.min_amount && Array.isArray(errors.rules) && errors.rules[idx]?.min_amount)}
                              helperText={Array.isArray(touched.rules) && touched.rules[idx]?.min_amount && Array.isArray(errors.rules) && errors.rules[idx]?.min_amount ? String(errors.rules[idx]?.min_amount) : ''}
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 6 }}>
                            <TextField
                              data-testid="newpkg-rate-edit-max-input"
                              name={`rules.${idx}.max_amount`}
                              label={tBilling('package_config.max_amt', { defaultValue: 'Max Amt' })}
                              size="small"
                              fullWidth
                              type="number"
                              placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                              value={rule?.max_amount ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!rule?.active}
                              error={Boolean(Array.isArray(touched.rules) && touched.rules[idx]?.max_amount && Array.isArray(errors.rules) && errors.rules[idx]?.max_amount)}
                              helperText={Array.isArray(touched.rules) && touched.rules[idx]?.max_amount && Array.isArray(errors.rules) && errors.rules[idx]?.max_amount ? String(errors.rules[idx]?.max_amount) : ''}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            
              {/* Add Tariff Section */}
              <Box sx={{ mb: 3 }}>
                <Grid2 container spacing={2} alignItems="center"  sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, md: 9 }}>
                    <Typography sx={{ fontWeight: 600, fontFamily: 'Montserrat', fontSize: 16 }}>{tBilling('package_config.add_tariff', { defaultValue: 'Add Tariff' })}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 3 }}>
                    <MultiSelectAutocompleteWithChips
                      data-testid="newpkg-tariff-select"
                      options={mappedTariffs || []}
                      value={values.tariffs || []}
                      onChange={(value) => {
                        void setFieldValue('tariffs', value);
                      }}
                      label={RequiredLabel(String(tBilling('package_config.select_add_tariff_type', { defaultValue: 'Select & Add Tariff Type' })), true)}
                      name="tariffs"
                      renderChip={() => null}
                      subLabelSx={filterInputSubLabelSx}
                      noOptionsText={tBilling('package_config.no_tariff_available', { defaultValue: 'No Tariff Available' })}
                      error={Boolean(touched.tariffs && errors.tariffs)}        
                    />
                  </Grid2>
                </Grid2>

                {/* Chips display below */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {(values.tariffs || []).map((tariff) => (
                    <SelectedChip
                      key={tariff.id}
                      selectedItem={tariff}
                      onDelete={(id) => {
                        const filtered = (values.tariffs || []).filter(t => t.id !== id);
                        handleChange({ target: { name: 'tariffs', value: filtered } });
                      }}
                    />
                  ))}
                </Box>

              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button data-testid="newpkg-cancel-button" variant="outlined" type="button" sx={{ minWidth: 100, borderRadius: 2 }} onClick={() => { navigate('/ai/configuration/package-configuration/package'); }}>{tBilling('package_config.cancel', { defaultValue: 'Cancel' })}</Button>
                <Button data-testid="newpkg-save-button" variant="contained" type="submit" sx={{ backgroundColor: '#444', minWidth: 100, borderRadius: 2, '&:hover': { backgroundColor: '#222' } }}>{tBilling('package_config.save_next', { defaultValue: 'Save & Next' })}</Button>
              </Box>
              <ConfirmationModal
                open={openModal}
                handleClose={handleCloseModal}
                onConfirm={handleConfirmedSubmit}
                type={'save'}
                title={tBilling('package_config.package_details', { defaultValue: 'Package Details' })}
                contentMessage={tBilling('package_config.messages.confirm_save_package_details', { defaultValue: 'Are you sure you want to save Package Details?' })}
              />
            </Form>
          );}}
      </Formik>
    </Box>
  );
};

export default NewPackage;
